/**
 * Recipe Detail Drawer Component
 *
 * Unified drawer with read and edit modes.
 * Sprint 6: Support for drafts, version navigation, and draft-specific actions.
 *
 * - Read mode: displays recipe info with locked fields
 * - Edit mode: unlocks fields for editing
 * - Draft mode: shows draft-specific actions (publish, delete)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertCircle, AlertTriangle, Package, FileEdit } from 'lucide-react';
import { Drawer, Badge } from '@/components/ui';
import { Input } from '@/components/ui';
import { Select, type SelectOption } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import {
  useRecipe,
  useRecipeVersions,
  useUpdateRecipe,
  useUpdateDraft,
  useAddRecipePosProduct,
  useRemoveRecipePosProduct,
  useCalculateRecipeCost,
  usePublishRecipe,
  useDeleteDraft,
  useCreateDraftFromActive,
} from '../hooks';
import { useRecipeBuilder } from '../hooks/useRecipeBuilder';
import { IngredientSearch } from './builder/IngredientSearch';
import { IngredientsTable } from './builder/IngredientsTable';
import { PosProductSelector } from './builder/PosProductSelector';
import { RecipeActionBar } from './RecipeActionBar';
import type { RawProductWithRelations, PosProductWithoutRecipe, RecipeVersionSummary } from '@bstock/shared';

interface RecipeDetailDrawerProps {
  recipeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

/**
 * Get badge variant for recipe status
 */
function getStatusBadgeVariant(recipe: { isDraft: boolean; validTo: string | null }): 'warning' | 'success' | 'neutral' {
  if (recipe.isDraft) return 'warning';
  if (recipe.validTo) return 'neutral';
  return 'success';
}

/**
 * Get status label for recipe
 */
function getStatusLabel(recipe: { isDraft: boolean; validTo: string | null }): string {
  if (recipe.isDraft) return 'Brouillon';
  if (recipe.validTo) return 'Archivée';
  return 'Active';
}

/**
 * Build select options from versions
 */
function buildVersionOptions(
  versions: RecipeVersionSummary[],
  formatDateShort: (date: string | Date | null | undefined) => string
): SelectOption[] {
  // Sort: draft first, then by version descending
  const sorted = [...versions].sort((a, b) => {
    if (a.isDraft && !b.isDraft) return -1;
    if (!a.isDraft && b.isDraft) return 1;
    return (b.version ?? 0) - (a.version ?? 0);
  });

  return sorted.map((v) => {
    let label: string;
    if (v.isDraft) {
      label = `Brouillon - Non publié`;
    } else if (v.validTo) {
      label = `V${v.version} - Du ${formatDateShort(v.validFrom)} au ${formatDateShort(v.validTo)}`;
    } else {
      label = `V${v.version} - Depuis le ${formatDateShort(v.validFrom)}`;
    }
    return { value: v.id, label };
  });
}

export function RecipeDetailDrawer({ recipeId, open, onOpenChange, showFinancials = false, canEdit = true, canDelete = true }: RecipeDetailDrawerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const { data: recipe, isLoading, error } = useRecipe(selectedVersionId ?? recipeId);
  const { data: versions } = useRecipeVersions(recipeId);

  // Mutations
  const updateMutation = useUpdateRecipe();
  const updateDraftMutation = useUpdateDraft();
  const calculateCost = useCalculateRecipeCost();
  const publishMutation = usePublishRecipe();
  const deleteDraftMutation = useDeleteDraft();
  const createDraftMutation = useCreateDraftFromActive();
  const addPosProductMutation = useAddRecipePosProduct();
  const removePosProductMutation = useRemoveRecipePosProduct();

  const builder = useRecipeBuilder();
  const currency = useCurrency();
  const { formatDateShort, formatDateTimeShort } = useFormatDate();

  // Reset state when drawer closes or recipe changes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setSelectedVersionId(null);
    }
  }, [open]);

  // Reset selected version when recipeId changes
  useEffect(() => {
    setSelectedVersionId(null);
  }, [recipeId]);

  // Load recipe into builder when fetched and in edit mode
  useEffect(() => {
    if (recipe && isEditing) {
      builder.setRecipe(recipe);
    }
  }, [recipe, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to new draft after creation
  useEffect(() => {
    if (createDraftMutation.isSuccess && createDraftMutation.data) {
      setSelectedVersionId(createDraftMutation.data.id);
      setIsEditing(true);
    }
  }, [createDraftMutation.isSuccess, createDraftMutation.data]);

  // === HANDLERS ===

  const handleVersionChange = useCallback((value: string) => {
    setSelectedVersionId(value);
    setIsEditing(false);
  }, []);

  const handleCalculateCost = useCallback(() => {
    if (recipe) {
      calculateCost.mutate(recipe.id);
    }
  }, [recipe, calculateCost]);

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
    // Reset builder to original recipe
    if (recipe) {
      builder.setRecipe(recipe);
    }
  }, [recipe, builder]);

  const handleAddIngredient = useCallback((product: RawProductWithRelations) => {
    builder.addIngredient(product);
  }, [builder]);

  const handleAddPosProduct = useCallback((product: PosProductWithoutRecipe) => {
    if (!recipe) return;
    addPosProductMutation.mutate({ recipeId: recipe.id, posProductId: product.id });
  }, [recipe, addPosProductMutation]);

  const handleRemovePosProduct = useCallback((productId: string) => {
    if (!recipe) return;
    removePosProductMutation.mutate({ recipeId: recipe.id, posProductId: productId });
  }, [recipe, removePosProductMutation]);

  const handleSave = useCallback(async () => {
    if (builder.hasErrors() || !recipe) {
      return;
    }

    const dto = builder.getUpdateDto();

    try {
      if (recipe.isDraft) {
        await updateDraftMutation.mutateAsync({
          id: recipe.id,
          data: dto,
        });
      } else {
        await updateMutation.mutateAsync({
          id: recipe.id,
          data: dto,
        });
      }
      setIsEditing(false);
    } catch {
      // Error handled by mutation
    }
  }, [builder, recipe, updateMutation, updateDraftMutation]);

  const handlePublish = useCallback(async () => {
    if (!recipe) return;
    try {
      await publishMutation.mutateAsync({ id: recipe.id });
      setIsEditing(false);
    } catch {
      // Error handled by mutation
    }
  }, [recipe, publishMutation]);

  const handleDelete = useCallback(async () => {
    if (!recipe) return;
    try {
      await deleteDraftMutation.mutateAsync(recipe.id);
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  }, [recipe, deleteDraftMutation, onOpenChange]);

  const handleCreateDraft = useCallback(() => {
    if (!recipe) return;
    createDraftMutation.mutate(recipe.id);
  }, [recipe, createDraftMutation]);


  // === COMPUTED VALUES ===

  // Format cost (prices are stored in cents)
  const formatCost = (cost: number | null | undefined) => {
    if (cost === null || cost === undefined) return '-';
    return formatCentimes(cost, currency);
  };

  // Build title with draft indicator
  const drawerTitle = isLoading
    ? 'Chargement...'
    : isEditing
      ? `Modifier: ${recipe?.name ?? 'Recette'}`
      : recipe?.name ?? 'Recette';

  // Build description with version info
  const drawerDescription = useMemo(() => {
    if (!recipe) return undefined;
    if (recipe.isDraft) {
      return `${recipe.recipeCode} - Brouillon`;
    }
    return `${recipe.recipeCode} - V${recipe.version}`;
  }, [recipe]);

  // Get excluded IDs for ingredient search
  const excludeIds = builder.ingredients
    .filter((i) => !i.isDeleted)
    .map((i) => i.rawProductId);

  // Calculate average sell price for display (when multiple products)
  const averageSellPrice = useMemo(() => {
    const products = recipe?.posProducts ?? [];
    const productsWithPrice = products.filter(p => p.sellPrice !== null);
    if (productsWithPrice.length === 0) return null;
    const total = productsWithPrice.reduce((sum, p) => sum + (p.sellPrice ?? 0), 0);
    return total / productsWithPrice.length;
  }, [recipe?.posProducts]);

  // Version select options
  const versionOptions = useMemo(() => {
    if (!versions || versions.length <= 1) return [];
    return buildVersionOptions(versions, formatDateShort);
  }, [versions, formatDateShort]);

  // Footer content with action bar
  const footerContent = recipe ? (
    <RecipeActionBar
      recipe={recipe}
      isEditing={isEditing}
      isDirty={builder.isDirty}
      isLoading={{
        publish: publishMutation.isPending,
        delete: deleteDraftMutation.isPending,
        calculate: calculateCost.isPending,
        createDraft: createDraftMutation.isPending,
        save: updateMutation.isPending || updateDraftMutation.isPending,
      }}
      showFinancials={showFinancials}
      canEdit={canEdit}
      canDelete={canDelete}
      onEdit={handleStartEditing}
      onCancelEdit={handleCancelEditing}
      onSave={handleSave}
      onPublish={handlePublish}
      onDelete={handleDelete}
      onCalculateCost={handleCalculateCost}
      onCreateDraft={handleCreateDraft}
    />
  ) : null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={drawerTitle}
      description={drawerDescription}
      size="xlarge"
      footer={footerContent}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      ) : error ? (
        <div className="text-center text-danger-500 py-8">
          Erreur lors du chargement: {error.message}
        </div>
      ) : recipe ? (
        <div className="space-y-6">
          {/* Header with version selector and status badge */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {versionOptions.length > 0 && (
                <Select
                  options={versionOptions}
                  value={selectedVersionId ?? recipe.id}
                  onChange={handleVersionChange}
                  disabled={isEditing}
                  className="w-auto min-w-[280px]"
                />
              )}
              <Badge variant={getStatusBadgeVariant(recipe)}>
                {getStatusLabel(recipe)}
              </Badge>
            </div>
          </div>

          {/* Validity period info - only for published versions */}
          {!recipe.isDraft && (
            <div className="bg-neutral-50 rounded-lg px-4 py-3 text-sm">
              {recipe.validTo ? (
                <span className="text-neutral-500">
                  En vigueur entre le <span className="font-medium text-dark-500">{formatDateTimeShort(recipe.validFrom)}</span> et le <span className="font-medium text-dark-500">{formatDateTimeShort(recipe.validTo)}</span>
                </span>
              ) : (
                <span className="text-neutral-500">
                  En vigueur depuis le <span className="font-medium text-dark-500">{formatDateTimeShort(recipe.validFrom)}</span>
                </span>
              )}
            </div>
          )}

          {/* Draft banner */}
          {recipe.isDraft && !isEditing && (
            <div className="bg-warning-50 border border-warning-200 rounded-lg px-4 py-3 flex items-center gap-2">
              <FileEdit className="w-4 h-4 text-warning-500" />
              <span className="text-sm text-warning-700">
                Ce brouillon n'est pas encore publié et n'apparaît pas dans les calculs de consommation.
              </span>
            </div>
          )}

          {isEditing ? (
            // EDIT MODE
            <div className="space-y-6">
              {/* Warning Banner */}
              {builder.isDirty && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg px-4 py-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-warning-500" />
                  <span className="text-sm text-warning-700">
                    Modifications non enregistrées
                  </span>
                </div>
              )}

              {/* Recipe Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nom de la recette
                </label>
                <Input
                  value={builder.name}
                  onChange={(e) => builder.updateRecipeInfo('name', e.target.value)}
                  placeholder="Nom de la recette"
                  error={builder.errors.name}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description
                </label>
                <Input
                  value={builder.description ?? ''}
                  onChange={(e) => builder.updateRecipeInfo('description', e.target.value || null)}
                  placeholder="Description (optionnel)"
                />
              </div>

              {/* POS Products Association - Only for non-drafts or when publishing */}
              {!recipe.isDraft && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Produits finis associés
                  </label>
                  <PosProductSelector
                    selectedProducts={recipe.posProducts ?? []}
                    onAddProduct={handleAddPosProduct}
                    onRemoveProduct={handleRemovePosProduct}
                  />
                  <p className="text-xs text-neutral-400 mt-1">
                    Associez cette recette à un ou plusieurs produits POS pour calculer leur coût théorique.
                  </p>
                </div>
              )}

              {/* Add Ingredient */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Ajouter un ingrédient
                </label>
                <IngredientSearch
                  onSelect={handleAddIngredient}
                  excludeIds={excludeIds}
                />
              </div>

              {/* Ingredients Table */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Ingrédients ({builder.activeIngredientsCount})
                </label>
                <IngredientsTable
                  ingredients={builder.ingredients}
                  onUpdateIngredient={builder.updateIngredient}
                  onRemoveIngredient={builder.removeIngredient}
                  errors={builder.errors}
                  showFinancials={showFinancials}
                />
                {builder.errors.ingredients && (
                  <p className="text-sm text-danger-500 mt-2">{builder.errors.ingredients}</p>
                )}
              </div>

              {/* Cost Summary - only for managers+ */}
              {showFinancials && (
                <div className="flex justify-between items-center p-4 bg-primary-50 rounded-lg">
                  <span className="font-medium text-dark-500">Coût total théorique</span>
                  <span className="text-xl font-bold text-primary-600">
                    {formatCost(builder.totalCost)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            // READ MODE
            <div className="space-y-6">
              {/* Info Section */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-dark-500 mb-3">Informations</h3>
                <div className="grid grid-cols-2 gap-4">
                  {showFinancials && (
                    <div>
                      <span className="text-sm text-neutral-500">Coût théorique</span>
                      <p className="font-medium text-dark-500">
                        {formatCost(recipe.theoreticalCost)}
                      </p>
                    </div>
                  )}
                  {recipe.yield && (
                    <div>
                      <span className="text-sm text-neutral-500">Rendement</span>
                      <p className="font-medium text-dark-500">
                        {recipe.yield} {recipe.yieldUnit?.symbol ?? ''}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-neutral-500">Dernière MAJ</span>
                    <p className="font-medium text-dark-500">
                      {formatDateShort(recipe.updatedAt)}
                    </p>
                  </div>
                </div>
                {recipe.description && (
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <span className="text-sm text-neutral-500">Description</span>
                    <p className="text-dark-500 mt-1">{recipe.description}</p>
                  </div>
                )}
              </div>

              {/* POS Products Section - Only for published recipes */}
              {!recipe.isDraft && (
                <div>
                  <h3 className="text-sm font-medium text-dark-500 mb-3">
                    Produits finis associés ({recipe.posProducts?.length ?? 0})
                  </h3>
                  {recipe.posProducts && recipe.posProducts.length > 0 ? (
                    <div className="space-y-2">
                      {recipe.posProducts.map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center gap-3 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg"
                        >
                          <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-4 h-4 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-dark-500 truncate">
                              {product.name}
                            </div>
                            {product.posCategory && (
                              <div className="text-xs text-neutral-400">
                                {product.posCategory}
                              </div>
                            )}
                          </div>
                          {showFinancials && product.sellPrice !== null && (
                            <div className="text-right">
                              <div className="font-medium text-dark-500">
                                {formatCentimes(product.sellPrice, currency)}
                              </div>
                              {recipe.theoreticalCost !== null && (
                                <div className="text-xs text-success-600">
                                  Marge: {Math.round(((product.sellPrice - recipe.theoreticalCost) / product.sellPrice) * 100)}%
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-3 bg-neutral-50 border border-dashed border-neutral-300 rounded-lg">
                      <Package className="w-4 h-4 text-neutral-400" />
                      <span className="text-sm text-neutral-500">Aucun produit fini associé</span>
                    </div>
                  )}
                </div>
              )}

              {/* Ingredients Section */}
              <div>
                <h3 className="text-sm font-medium text-dark-500 mb-3">
                  Ingrédients ({recipe.ingredients.length})
                </h3>
                {recipe.ingredients.length > 0 ? (
                  <div className="border border-neutral-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200">
                          <th className="text-left px-4 py-2 text-xs font-medium text-neutral-500">
                            Produit brut
                          </th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500">
                            Quantité
                          </th>
                          {showFinancials && (
                            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500">
                              Coût
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {recipe.ingredients.map((ingredient) => (
                          <tr key={ingredient.id} className={`border-b border-neutral-100 last:border-b-0 ${!ingredient.rawProduct.isActive ? 'bg-warning-50/50' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-dark-500">
                                  {ingredient.rawProduct.name}
                                </span>
                                {!ingredient.rawProduct.isActive && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-700">
                                    <AlertTriangle className="w-3 h-3" />
                                    Archive
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-neutral-400">
                                {ingredient.rawProduct.sku}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-dark-500">
                              {formatQuantity(ingredient.quantity)} {ingredient.rawProduct.recipeUnit.symbol}
                            </td>
                            {showFinancials && (
                              <td className="px-4 py-3 text-right">
                                {ingredient.cost !== null && ingredient.cost !== undefined ? (
                                  <span className="text-dark-500">{formatCost(ingredient.cost)}</span>
                                ) : (
                                  <span className="text-warning-500 text-sm">PMP manquant</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-3 bg-neutral-50 border border-dashed border-neutral-300 rounded-lg">
                    <span className="text-sm text-neutral-500">Aucun ingrédient</span>
                  </div>
                )}
              </div>

              {/* Total Cost - only for managers+ */}
              {showFinancials && (
                <div className="flex justify-between items-center p-4 bg-primary-50 rounded-lg">
                  <span className="font-medium text-dark-500">Coût total théorique</span>
                  <span className="text-xl font-bold text-primary-600">
                    {formatCost(recipe.theoreticalCost)}
                  </span>
                </div>
              )}

              {/* Margin Summary (if products have prices) - Only for managers+ and published */}
              {showFinancials && !recipe.isDraft && averageSellPrice !== null && recipe.theoreticalCost !== null && (
                <div className="p-4 bg-neutral-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Prix de vente moyen</span>
                    <span className="font-medium text-dark-500">
                      {formatCentimes(averageSellPrice, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-neutral-200">
                    <span className="text-neutral-600">Marge théorique moyenne</span>
                    <span className="font-medium text-success-600">
                      {formatCentimes(averageSellPrice - recipe.theoreticalCost, currency)}
                      <span className="text-sm ml-1">
                        ({Math.round(((averageSellPrice - recipe.theoreticalCost) / averageSellPrice) * 100)}%)
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Drawer>
  );
}
